import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brush, Eye, CheckCircle2, RotateCcw, Sun, Moon, Monitor } from "lucide-react";

const MODES = [
  { key: "light", label: "Claro", icon: Sun },
  { key: "dark", label: "Escuro", icon: Moon },
  { key: "", label: "Padrão do sistema", icon: Monitor },
];

function Swatches({ theme, mode }) {
  const pal = theme?.[mode === "dark" ? "dark" : "light"] || {};
  return (
    <div className="flex gap-1.5">
      {["primary", "background", "card", "foreground", "chart_2"].map((k) => (
        <span key={k} className="h-5 w-5 rounded-full border border-border" style={{ background: pal[k] }} title={k} />
      ))}
    </div>
  );
}

export default function MyAppearance({ theme: mode, setTheme }) {
  const { applyUserTheme, clearUserTheme, refresh } = useBranding();
  const [data, setData] = useState({ themes: [], theme_id: null, global_theme_id: null, mode: null });
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(null);

  const load = useCallback(() => api.get("/themes/my-library").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async (theme_id, newMode) => {
    setBusy(true);
    try {
      const { data: res } = await api.put("/themes/my-preference", { theme_id, mode: newMode ?? data.mode ?? null });
      if (res.theme) applyUserTheme(res.theme); else { clearUserTheme(); await refresh(); }
      if (res.mode) setTheme(res.mode);
      setPreviewing(null);
      setData((d) => ({ ...d, theme_id: res.theme_id, mode: res.mode }));
      toast.success("Sua aparência foi salva — visível somente para você");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const preview = (t) => { applyUserTheme(t); setPreviewing(t); toast.info(`Pré-visualizando "${t.name}" — nada foi salvo ainda`); };
  const cancelPreview = async () => { clearUserTheme(); setPreviewing(null); await load(); const cur = data.themes.find((t) => t.id === data.theme_id); if (cur) applyUserTheme(cur); };

  const categories = [...new Set(data.themes.map((t) => t.category))];

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="my-appearance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Brush size={26} className="text-primary" /> Minha Aparência
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Personalização individual: altera somente a sua interface. A identidade visual global do ERP continua sob controle do administrador.
          </p>
        </div>
        {previewing && (
          <div className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2" data-testid="my-preview-banner">
            <Eye size={14} className="text-primary" />
            <span className="text-xs">Pré-visualizando <b>{previewing.name}</b></span>
            <Button size="sm" className="h-7" disabled={busy} onClick={() => save(previewing.id)} data-testid="my-preview-save-btn">Usar este tema</Button>
            <Button size="sm" variant="ghost" className="h-7" onClick={cancelPreview} data-testid="my-preview-cancel-btn">Descartar</Button>
          </div>
        )}
      </div>

      <Card className="p-5 border-border bg-card space-y-3" data-testid="my-mode-card">
        <h3 className="font-heading font-semibold text-sm">Modo de exibição</h3>
        <div className="flex flex-wrap gap-2">
          {MODES.map(({ key, label, icon: Icon }) => (
            <Button key={label} variant={(data.mode || "") === key ? "default" : "outline"} size="sm"
              disabled={busy} onClick={() => save(data.theme_id ?? null, key || null)} data-testid={`my-mode-${key || "system"}`}>
              <Icon size={13} className="mr-1.5" /> {label}
            </Button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">O padrão do sistema é o tema claro executivo. O modo escuro fica disponível como preferência sua.</p>
      </Card>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={busy} onClick={() => save(null, data.mode || null)} data-testid="my-theme-reset-btn">
          <RotateCcw size={13} className="mr-1.5" /> Voltar ao tema da empresa
        </Button>
        {data.theme_id && <Badge variant="outline" className="text-[10px]" data-testid="my-theme-active-badge">Tema pessoal ativo</Badge>}
      </div>

      {categories.map((cat) => (
        <div key={cat}>
          <h3 className="font-heading font-semibold text-sm mb-3">{cat}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.themes.filter((t) => t.category === cat).map((t) => (
              <Card key={t.id} className={`p-4 border-border bg-card space-y-3 ${data.theme_id === t.id ? "border-primary" : ""}`} data-testid={`my-theme-card-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-sm truncate">{t.name}</p>
                  {data.theme_id === t.id && <Badge className="bg-primary/15 text-primary border-primary/30" variant="outline"><CheckCircle2 size={10} className="mr-1" /> Seu tema</Badge>}
                  {data.global_theme_id === t.id && data.theme_id !== t.id && <Badge variant="outline" className="text-[9px]">Padrão da empresa</Badge>}
                </div>
                <Swatches theme={t} mode={mode} />
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => preview(t)} data-testid={`my-theme-preview-${t.id}`}><Eye size={11} className="mr-1" /> Prévia</Button>
                  <Button size="sm" className="h-7 text-xs" disabled={busy || data.theme_id === t.id} onClick={() => save(t.id)} data-testid={`my-theme-apply-${t.id}`}>Usar</Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
